import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  PenSquare,
  Clock,
  AlertTriangle,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  FileText,
  Loader2,
} from 'lucide-react';
import { Button } from '../components/shared/Button';
import { ExamContentGuard } from '../components/exam/ExamContentGuard';
import { DevToolsBlockOverlay } from '../components/exam/DevToolsBlockOverlay';
import { useDevToolsGuard } from '../hooks/useDevToolsGuard';
import { examService, type ResumeAttemptResponse } from '../services/exam.service';

interface WritingTask {
  id: string;
  title: string;
  prompt: string;
  context: string;
  minWords: number;
  maxWords: number;
}

function mapResumeToTasks(data: ResumeAttemptResponse): WritingTask[] {
  const exam = data.exam;
  if (!exam?.questions?.length) return [];
  return exam.questions.map((q) => ({
    id: q._id,
    title: q.passageTitle || (q.prompt ? q.prompt.slice(0, 50) + (q.prompt.length > 50 ? '…' : '') : q.text?.slice(0, 50) || 'Task'),
    prompt: q.prompt || q.text || '',
    context: q.context || q.text || '',
    minWords: q.minWords ?? 50,
    maxWords: q.maxWords ?? 200,
  }));
}

function getInitialAnswers(data: ResumeAttemptResponse): Record<string, string> {
  const out: Record<string, string> = {};
  const exam = data.exam;
  if (!exam?.questions?.length) return out;
  const saved = data.savedAnswers || [];
  exam.questions.forEach((q) => {
    const savedEntry = saved.find((a) => String(a.questionId) === String(q._id));
    if (savedEntry != null && typeof savedEntry.answer === 'string') {
      out[q._id] = savedEntry.answer;
    }
  });
  return out;
}

const SAVE_DEBOUNCE_MS = 600;

export const WritingExamPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const attemptId = searchParams.get('attemptId');
  const { devToolsAllowed, devToolsOpen } = useDevToolsGuard(searchParams);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [resumeData, setResumeData] = useState<ResumeAttemptResponse | null>(null);
  const [tasks, setTasks] = useState<WritingTask[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const answersRef = useRef<Record<string, string>>({});
  const pendingSavesRef = useRef<Set<Promise<void>>>(new Set());
  const submittingRef = useRef(false);

  useEffect(() => {
    if (!attemptId) {
      navigate('/dashboard');
      return;
    }
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    examService
      .resumeAttempt(attemptId)
      .then((data) => {
        if (cancelled) return;
        if (data.exam?.type !== 'writing') {
          setLoadError('This is not a writing exam.');
          setLoading(false);
          return;
        }
        const mapped = mapResumeToTasks(data);
        if (mapped.length === 0) {
          setLoadError('No tasks in this exam.');
          setLoading(false);
          return;
        }
        setResumeData(data);
        setTasks(mapped);
        const initialAnswers = getInitialAnswers(data);
        answersRef.current = initialAnswers;
        setAnswers(initialAnswers);
        const remaining = Math.max(
          0,
          Math.floor((new Date(data.expiresAt).getTime() - Date.now()) / 1000),
        );
        setRemainingSeconds(remaining);
        setLoading(false);
      })
      .catch((err: any) => {
        if (cancelled) return;
        setLoadError(
          err?.response?.data?.message || 'Could not load exam. You may need to start again.',
        );
        setLoading(false);
      });
    return () => {
      cancelled = true;
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, [attemptId, navigate]);

  const current = tasks[currentIndex];
  const text = current ? (answers[current.id] || '') : '';
  const wordCount = useMemo(() => {
    const trimmed = text.trim();
    return trimmed.length ? trimmed.split(/\s+/).length : 0;
  }, [text]);
  const wordCountStatus = useMemo(() => {
    const min = current?.minWords ?? 50;
    const max = current?.maxWords ?? 200;
    if (wordCount < min) return 'low';
    if (wordCount > max) return 'high';
    return 'ok';
  }, [wordCount, current?.minWords, current?.maxWords]);

  const handleSubmit = useCallback(async () => {
    if (!attemptId || submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    setSubmitError(null);
    try {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
      // Older debounced requests must settle before writing the final text.
      await Promise.all(Array.from(pendingSavesRef.current));
      for (const [questionId, answer] of Object.entries(answersRef.current)) {
        await examService.saveAnswer(attemptId, questionId, answer);
      }
      await examService.submitExam(attemptId);
      navigate(`/results?attemptId=${attemptId}`);
    } catch (err: any) {
      submittingRef.current = false;
      setSubmitting(false);
      setSubmitError(err?.response?.data?.message || t('exam.common.failedToSubmit'));
      setShowSubmitModal(true);
    }
  }, [attemptId, navigate, t]);

  useEffect(() => {
    if (!resumeData || tasks.length === 0) return;
    const expiresAt = new Date(resumeData.expiresAt).getTime();
    const timer = setInterval(() => {
      const remaining = Math.max(0, Math.floor((expiresAt - Date.now()) / 1000));
      setRemainingSeconds(remaining);
      if (remaining <= 0) {
        clearInterval(timer);
        void handleSubmit();
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [resumeData, tasks.length, handleSubmit]);

  const formatTime = (totalSeconds: number) => {
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    if (h > 0) {
      return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    }
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const saveAnswerToBackend = useCallback(
    (questionId: string, answerText: string) => {
      if (!attemptId) return;
      const pending = examService.saveAnswer(attemptId, questionId, answerText)
        .then(() => undefined)
        .catch(() => undefined);
      pendingSavesRef.current.add(pending);
      void pending.then(() => pendingSavesRef.current.delete(pending));
    },
    [attemptId],
  );

  const handleChange = useCallback(
    (value: string) => {
      if (!current) return;
      answersRef.current = { ...answersRef.current, [current.id]: value };
      setAnswers((prev) => ({ ...prev, [current.id]: value }));
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = setTimeout(() => {
        saveAnswerToBackend(current.id, value);
        saveTimeoutRef.current = null;
      }, SAVE_DEBOUNCE_MS);
    },
    [current, saveAnswerToBackend],
  );

  const navigateTask = (offset: number) => {
    setCurrentIndex((prev) => {
      const next = prev + offset;
      if (next < 0 || next >= tasks.length) return prev;
      return next;
    });
  };

  const answeredCount = Object.values(answers).filter((v) => v.trim().length > 0).length;
  const isLowTime = remainingSeconds < 300;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-10 h-10 animate-spin text-primary" />
          <p className="text-gray-600">Loading exam…</p>
        </div>
      </div>
    );
  }

  if (loadError || tasks.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
        <div className="bg-white rounded-2xl shadow-lg p-6 max-w-md text-center">
          <p className="text-gray-800 font-medium mb-4">{loadError || 'No tasks available.'}</p>
          <Button variant="primary" onClick={() => navigate('/dashboard')}>
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-emerald-50 to-white">
      <DevToolsBlockOverlay show={devToolsOpen} />
      <div className="sticky top-0 z-50 bg-white shadow-md">
        <div className="max-w-6xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                <PenSquare className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">{t('exam.writing.title')}</h1>
                <p className="text-xs text-gray-500">
                  {resumeData?.exam?.level || ''} · {tasks.length} {t('exam.writing.writingTasks')}
                </p>
              </div>
            </div>
            <div
              className={`flex items-center gap-2 px-4 py-2 rounded-xl font-mono text-lg ${
                isLowTime ? 'bg-error/10 text-error animate-pulse' : 'bg-gray-900 text-white'
              }`}
            >
              <Clock className="w-5 h-5" />
              <span className="font-bold">{formatTime(remainingSeconds)}</span>
            </div>
          </div>
          <div className="mt-3 flex items-center gap-3">
            <div className="flex-1 bg-gray-200 h-2 rounded-full">
              <div
                className="h-2 rounded-full bg-emerald-500 transition-all duration-300"
                style={{ width: `${(answeredCount / tasks.length) * 100}%` }}
              />
            </div>
            <span className="text-sm text-gray-600 whitespace-nowrap">
              {answeredCount}/{tasks.length} {t('exam.common.completed')}
            </span>
          </div>
        </div>
      </div>

      <main className="flex-1 max-w-6xl mx-auto px-4 py-6 w-full">
        {/* AI Disclosure (EU AI Act Art. 50) */}
        <div className="mb-4 flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-900">
          <span className="shrink-0 font-bold mt-0.5">🤖</span>
          <span>
            <strong>AI-beoordeling:</strong> Jouw schrijftekst wordt automatisch beoordeeld via AI (OpenAI GPT). De score is indicatief en vervangt geen officiële examinering.
          </span>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="lg:col-span-3 space-y-4">
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <span className="px-3 py-1 bg-emerald-100 text-emerald-700 font-bold rounded-full text-sm">
                    {t('exam.common.taskOf', { current: currentIndex + 1, total: tasks.length })}
                  </span>
                  <h2 className="text-lg font-bold text-gray-900">{current.title}</h2>
                </div>
              </div>
              <ExamContentGuard disabled={devToolsAllowed}>
                {current.context && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
                    <div className="flex items-start gap-2">
                      <FileText className="w-5 h-5 text-amber-600 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-amber-800">{t('exam.common.situation')}</p>
                        <p className="text-sm text-amber-700">{current.context}</p>
                      </div>
                    </div>
                  </div>
                )}
                <div className="bg-gray-50 rounded-xl p-4 mb-4">
                  <p className="text-gray-900 font-medium">{current.prompt}</p>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <span>{t('exam.writing.recommendedWords')}</span>
                  <span className="font-bold text-gray-900">
                    {current.minWords} - {current.maxWords}
                  </span>
                </div>
              </ExamContentGuard>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="mb-3 flex items-center justify-between">
                <label className="font-medium text-gray-700">{t('exam.common.yourAnswer')}</label>
                <div
                  className={`px-3 py-1 rounded-full text-sm font-medium ${
                    wordCountStatus === 'ok'
                      ? 'bg-success/10 text-success'
                      : wordCountStatus === 'low'
                        ? 'bg-warning/10 text-warning'
                        : 'bg-error/10 text-error'
                  }`}
                >
                  {wordCount} {t('exam.common.words')}
                  {wordCountStatus === 'low' && ` (${t('exam.common.min')}: ${current.minWords})`}
                  {wordCountStatus === 'high' && ` (${t('exam.common.max')}: ${current.maxWords})`}
                </div>
              </div>
              <textarea
                value={text}
                disabled={submitting}
                onChange={(e) => handleChange(e.target.value)}
                className="w-full h-72 border-2 border-gray-200 rounded-xl p-4 text-gray-900 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 resize-none font-sans leading-relaxed"
                placeholder={t('exam.writing.placeholder')}
              />
              <div className="mt-3">
                <div className="w-full bg-gray-200 h-2 rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all duration-300 ${
                      wordCountStatus === 'ok' ? 'bg-success' : wordCountStatus === 'low' ? 'bg-warning' : 'bg-error'
                    }`}
                    style={{ width: `${Math.min(100, (wordCount / current.maxWords) * 100)}%` }}
                  />
                </div>
                <div className="flex justify-between mt-1 text-xs text-gray-500">
                  <span>0</span>
                  <span className="text-emerald-600 font-medium">{current.minWords}</span>
                  <span className="text-emerald-600 font-medium">{current.maxWords}</span>
                </div>
              </div>
            </div>

            <div className="flex justify-between">
              <Button
                variant="tertiary"
                onClick={() => navigateTask(-1)}
                disabled={currentIndex === 0}
                icon={<ChevronLeft className="w-4 h-4" />}
              >
                {t('exam.common.previousTask')}
              </Button>
              <div className="flex gap-3">
                {currentIndex < tasks.length - 1 ? (
                  <Button
                    variant="primary"
                    onClick={() => navigateTask(1)}
                    className="flex-row-reverse"
                    icon={<ChevronRight className="w-4 h-4" />}
                  >
                    {t('exam.common.nextTask')}
                  </Button>
                ) : (
                  <Button
                    variant="primary"
                    className="bg-success hover:bg-success/90"
                    onClick={() => setShowSubmitModal(true)}
                    icon={<CheckCircle className="w-4 h-4" />}
                  >
                    {t('exam.common.submitExam')}
                  </Button>
                )}
              </div>
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-lg p-4 sticky top-32">
              <h3 className="font-bold text-gray-900 mb-3">{t('exam.common.tasks')}</h3>
              <div className="space-y-2 mb-4">
                {tasks.map((task, idx) => {
                  const taskText = answers[task.id] || '';
                  const taskWordCount = taskText.trim().length ? taskText.trim().split(/\s+/).length : 0;
                  const isTaskComplete = taskWordCount >= task.minWords;
                  const isCurrent = idx === currentIndex;
                  return (
                    <button
                      key={task.id}
                      onClick={() => setCurrentIndex(idx)}
                      className={`w-full text-left px-3 py-2 rounded-lg transition-all ${
                        isCurrent
                          ? 'bg-emerald-100 border-2 border-emerald-500'
                          : isTaskComplete
                            ? 'bg-success/10 border border-success/30'
                            : 'bg-gray-50 border border-gray-200 hover:bg-gray-100'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className={`text-sm font-medium ${isCurrent ? 'text-emerald-700' : 'text-gray-700'}`}>
                          {idx + 1}. {task.title}
                        </span>
                        {isTaskComplete && <CheckCircle className="w-4 h-4 text-success" />}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {taskWordCount} / {task.minWords}-{task.maxWords} {t('exam.common.words')}
                      </div>
                    </button>
                  );
                })}
              </div>
              <div className="bg-amber-50 rounded-xl p-3 mb-4">
                <p className="text-xs font-medium text-amber-800 mb-2">💡 {t('exam.common.tips')}</p>
                <ul className="text-xs text-amber-700 space-y-1">
                  <li>• {t('exam.writing.tips.readCarefully')}</li>
                  <li>• {t('exam.writing.tips.greeting')}</li>
                  <li>• {t('exam.writing.tips.spelling')}</li>
                  <li>• {t('exam.writing.tips.wordCount')}</li>
                </ul>
              </div>
              <Button
                variant="primary"
                className="w-full bg-success hover:bg-success/90"
                onClick={() => setShowSubmitModal(true)}
              >
                {t('exam.common.submitExam')}
              </Button>
            </div>
          </div>
        </div>
      </main>

      {showSubmitModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-warning/10 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-warning" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">{t('exam.common.confirmSubmit')}</h3>
                <p className="text-sm text-gray-500">{t('exam.common.cannotUndo')}</p>
              </div>
            </div>
            <div className="bg-gray-50 rounded-xl p-4 mb-6">
              {tasks.map((task, idx) => {
                const taskText = answers[task.id] || '';
                const taskWordCount = taskText.trim().length ? taskText.trim().split(/\s+/).length : 0;
                const isTaskComplete = taskWordCount >= task.minWords;
                return (
                  <div key={task.id} className="flex justify-between items-center py-1">
                    <span className="text-sm text-gray-600">{t('exam.common.task')} {idx + 1}:</span>
                    <span className={`text-sm font-medium ${isTaskComplete ? 'text-success' : 'text-error'}`}>
                      {taskWordCount} {t('exam.common.words')} {isTaskComplete ? '✓' : '✗'}
                    </span>
                  </div>
                );
              })}
            </div>
            {answeredCount < tasks.length && (
              <p className="text-sm text-error mb-4">
                ⚠️ {t('exam.common.incompleteWarning', { count: tasks.length - answeredCount })}
              </p>
            )}
            {submitError && <p role="alert" className="text-sm text-error mb-4">{submitError}</p>}
            <div className="flex gap-3">
              <Button
                variant="tertiary"
                className="flex-1"
                onClick={() => setShowSubmitModal(false)}
                disabled={submitting}
              >
                {t('exam.common.backToExam')}
              </Button>
              <Button
                variant="primary"
                className="flex-1 bg-success hover:bg-success/90"
                loading={submitting}
                onClick={handleSubmit}
                icon={<CheckCircle className="w-4 h-4" />}
              >
                {t('exam.common.submit')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WritingExamPage;
